"use client";

import { IconChevronDown } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Accordion } from "radix-ui";
import { useMemo, useRef, type ReactNode } from "react";
import { useSecondaryLayers } from "@/features/layers/useSecondaryLayers";
import { visibleLegendSections } from "@/features/layers/legendSwatches";
import { useProjectText } from "@/features/project/useProjectText";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import type { MapLegendItem } from "@/types";
import { LegendItem } from "./LegendItem";

/**
 * The legend panel, in two halves.
 *
 * First the images the tenant authored — each a full-width key produced by hand —
 * then a section per group of secondary layers that are switched on, with a
 * swatch derived from each layer's own paint properties. The second half changes
 * as the user toggles layers, which is the point: the legend describes what is on
 * the map now, not what could be.
 */
export function LegendDropdown({
  items,
  onClose,
}: {
  items: MapLegendItem[];
  onClose: () => void;
}) {
  const t = useTranslations();
  const projectText = useProjectText();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, true, onClose);

  const { sections, isActive } = useSecondaryLayers();
  const live = useMemo(
    () => visibleLegendSections(sections, isActive),
    [sections, isActive]
  );

  return (
    <div
      ref={ref}
      className="absolute left-0 ml-[11px] mt-[10px] min-w-24 rounded-b-xl border border-t-0 border-gray-300 bg-white shadow-md dark:bg-dark-color dark:shadow-md"
    >
      <Accordion.Root type="single" defaultValue="accordion-item-0" collapsible>
        {items.map(({ title, url }, index) => {
          const label = projectText(title, title ?? "");

          return (
            <Section
              key={`accordion-item-${index}`}
              value={`accordion-item-${index}`}
              title={label}
              // A single authored image with nothing beside it is the whole
              // legend, so there is nothing to choose between.
              showTrigger={items.length > 1 || live.length > 0}
            >
              {/* Tenant-authored legend image of arbitrary dimensions. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={url} alt={label} className="w-full" loading="lazy" />
            </Section>
          );
        })}

        {items.length > 0 && live.length > 0 && (
          <div className="mx-3 border-t border-gray-200 dark:border-gray-700" />
        )}

        {live.map(({ section, layers }, index) => (
          <Section
            key={`section-${section.id ?? index}`}
            value={`section-${section.id ?? index}`}
            title={projectText(
              `map.layers.sections.section_${section.id}`,
              section.id ?? ""
            )}
            showTrigger
          >
            <div className="space-y-2">
              {layers.map(({ layer, styleLayers }) => {
                const label = projectText(
                  `map.layers.${layer.name}`,
                  layer.name ?? ""
                );

                return (
                  <LegendItem key={layer.name} styleLayers={styleLayers}>
                    <div
                      className="whitespace-normal break-words text-left text-xs leading-tight"
                      title={label}
                    >
                      {label}
                    </div>
                  </LegendItem>
                );
              })}
            </div>
          </Section>
        ))}

        {items.length === 0 && live.length === 0 && (
          <p className="p-4 text-sm opacity-60">{t("legendEmpty")}</p>
        )}
      </Accordion.Root>
    </div>
  );
}

function Section({
  value,
  title,
  showTrigger,
  children,
}: {
  value: string;
  title: string;
  showTrigger: boolean;
  children: ReactNode;
}) {
  return (
    <Accordion.Item value={value}>
      {showTrigger && (
        <Accordion.Trigger className="group flex w-full items-center justify-between gap-2 whitespace-nowrap p-3 transition-all duration-300 hover:bg-primary data-[state=open]:bg-primary data-[state=open]:dynamic-text dark:hover:bg-primary">
          <span className="font-semibold">{title}</span>
          <IconChevronDown
            className="group-data-[state=open]:rotate-180"
            size={16}
            aria-hidden="true"
          />
        </Accordion.Trigger>
      )}

      <Accordion.Content>
        <div className="rounded-b-lg bg-white p-4 text-black dark:bg-dark-color dark:text-white">
          {children}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
}
